import { useState } from "react"

import { prefetchCommonUseOfFundsListQuery } from "@boundless/bl-api-client/common/services"
import {
  useCustomerFundingNeedsAllowedRetrieveSuspense,
  useCustomerFundingNeedsListSuspense,
} from "@boundless/bl-api-client/customer/services"
import {
  createFileRoute,
  redirect,
  retainSearchParams,
  useNavigate,
} from "@tanstack/react-router"

import { FaqCard } from "@/components/funding-requests/FaqCard"
import { FundingRequestCard } from "@/components/funding-requests/FundingRequestCard"
import { Button } from "@/components/ui/button"

export const Route = createFileRoute("/_dashboard/funding-requests/")({
  search: {
    middlewares: [retainSearchParams(true)],
  },
  beforeLoad: ({ context: { store } }) => {
    const companyUuid = store.getState().activeCompanyUuid
    if (!companyUuid) {
      throw redirect({ to: "/home" })
    }
    return { companyUuid }
  },
  loader: ({ context: { queryClient } }) => {
    prefetchCommonUseOfFundsListQuery(queryClient)
  },
  component: FundingRequestsIndex,
})

function FundingRequestsIndex() {
  const navigate = useNavigate()
  const { companyUuid } = Route.useRouteContext()

  const [activeTab, setActiveTab] = useState<"live" | "archived">("live")

  const { data: fundingNeeds } =
    useCustomerFundingNeedsListSuspense(companyUuid)

  const {
    data: { isAllowed },
  } = useCustomerFundingNeedsAllowedRetrieveSuspense(companyUuid)

  const filterData = () => {
    if (activeTab === "archived") {
      return fundingNeeds.filter((item) => item.status === "expired")
    }
    return fundingNeeds.filter((item) => item.status === "live")
  }

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <div className="order-1 w-full lg:order-2 lg:w-1/4">
        <FaqCard className="w-full lg:h-3/4" />
      </div>

      <div className="order-2 w-full lg:order-1 lg:w-3/4">
        <div className="rounded-lg bg-white p-4 text-sm shadow-sm sm:p-6">
          <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-center xl:gap-4">
            <Button
              onClick={() => navigate({ to: "/funding-requests/create" })}
              className="w-full xl:ml-auto xl:w-auto"
              disabled={!isAllowed}
            >
              Create New Funding Request
            </Button>

            <div className="order-first grid grid-cols-2 gap-2 sm:gap-3 lg:flex lg:justify-center lg:gap-8 xl:justify-start">
              <Button
                onClick={() => setActiveTab("live")}
                variant="ghost"
                className={`w-full justify-center pb-2 text-base font-medium transition-colors sm:text-lg lg:w-auto lg:text-2xl ${
                  activeTab === "live"
                    ? "border-gray-900 text-gray-900"
                    : "border-transparent text-gray-400 hover:text-gray-600"
                }`}
              >
                Live Requests
              </Button>

              <Button
                onClick={() => setActiveTab("archived")}
                variant="ghost"
                className={`w-full justify-center pb-2 text-base font-medium transition-colors sm:text-lg lg:w-auto lg:text-2xl ${
                  activeTab === "archived"
                    ? "border-gray-900 text-gray-900"
                    : "border-transparent text-gray-400 hover:text-gray-600"
                }`}
              >
                Archived
              </Button>
            </div>
          </div>

          <div className="@container grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
            {filterData().map((item) => (
              <div className="min-w-0" key={item.uuid}>
                <FundingRequestCard item={item} companyUuid={companyUuid} />
              </div>
            ))}
          </div>

          {fundingNeeds.length === 0 && (
            <div className="py-12 text-center">
              <p className="text-gray-500">
                No {activeTab} funding requests found.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
